<template>
  <div class="col-12">
    <input-group v-model="config.name" placeholder="Enter A/B Test Name" label="Name" />
    <div style="color: white">
      Automated Ab Push id: {{ this.$route.params.id }}
    </div>
    <div v-if="initialMessageState" class="alert alert-info mt-3">
      Editing Automated A/B Test message: <strong>{{ initialMessageState.name }}</strong> (Status: {{ initialMessageState.status }})
    </div>
    <div v-if="initialMessageState && initialMessageState.audience_count && initialMessageState.audience_count.status === 'DONE' && !isAudienceUpdated" class="alert alert-success mt-3">
      <i class="fas fa-check-circle"></i>
      This Automated A/B Test message has a pre-calculated audience count of <strong>{{ initialMessageState.audience_count.value }}</strong>.
    </div>
    <audience-section v-model="config.audience" title="1.Audience" />
    <div class="template-picker">
      <div>
        Test A
      </div>
      <select v-model="config.messages[0]" class="form-control">
        <option disabled value="">Pick Test A</option>
        <option v-for="template in templates" v-bind:key="template.name"
          :value="{ ...template.messages[0], templateId: template.id }">{{ template.name
          }}</option>
      </select>
    </div>
    <message-section v-on:updatedLanguages="updateLanguages" v-if="config.messages[0]" v-model="config.messages[0]"  :isAb=true />
    <div class="template-picker">
      <div>
        Test B
      </div>
      <select v-model="config.messages[1]" class="form-control">
        <option disabled value="">Pick Test B</option>
        <option v-for="template in templates" v-bind:key="template.name"
          :value="{ ...template.messages[0], templateId: template.id }">{{ template.name
          }}</option>
      </select>
    </div>
    <message-section v-on:updatedLanguages="updateLanguages" v-if="config.messages[1]" v-model="config.messages[1]"  :isAb=true />
    <schedule-section automated v-model="config.schedule" title="4.Schedule" :hideDateTime="!!config.schedule.abSendTime" />
    <div class="segment" v-if="!config.schedule.sendImmediately">
      <div class="title">Per-Variant Settings</div>
      <label class="ab-sendtime-toggle">
        <input type="checkbox"
               :checked="!!config.schedule.abSendTime"
               @change="onAbSendTimeToggle($event.target.checked)" />
        Send variants at different times (A/B test delivery time)
      </label>
      <template v-if="config.schedule.abSendTime">
        <div class="variant-settings-row">
          <label class="variant-schedule-label">Variant {{ (config.messages[0] && config.messages[0].variantName) || 'A' }}</label>
          <datetime class="schedule-datetime" type="datetime" step="1"
            :value="variantASendTimeISO" @input="updateVariantASendTime"
            placeholder="Select delivery time"
            :min-datetime="new Date().toISOString()"
            :max-datetime="new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString()" />
          <label class="variant-holdback-toggle"
                 title="Users are counted in this variant but receive no push">
            <input type="checkbox"
                   :checked="!!(config.messages[0] && config.messages[0].isHoldback)"
                   @change="updateVariantAHoldback($event.target.checked)" />
            Holdback
          </label>
        </div>
        <div class="variant-settings-row">
          <label class="variant-schedule-label">Variant {{ (config.messages[1] && config.messages[1].variantName) || 'B' }}</label>
          <datetime class="schedule-datetime" type="datetime" step="1"
            :value="variantBSendTimeISO" @input="updateVariantBSendTime"
            placeholder="Select delivery time"
            :min-datetime="new Date().toISOString()"
            :max-datetime="new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString()" />
          <label class="variant-holdback-toggle"
                 title="Users are counted in this variant but receive no push">
            <input type="checkbox"
                   :checked="!!(config.messages[1] && config.messages[1].isHoldback)"
                   @change="updateVariantBHoldback($event.target.checked)" />
            Holdback
          </label>
        </div>
        <div v-if="variantDelayWarning" class="alert alert-warning mt-2">
          {{ variantDelayWarning }}
        </div>
      </template>
    </div>
    <div id="spinner" class="spinner" v-show="showSpinner">
      <Spinner></Spinner>
    </div>
    <div class="message-buttons col-12">
      <div class="message-button" id="button-confirm" @click="openConfirmModal" :class="{ disabled: isConfirmDisabled }" :aria-disabled="isConfirmDisabled">
        CONFIRM
      </div>
      <div class="message-button" id="button-draft" @click="submitAutomatedABTest(true)">
        {{ draftButtonLabel }}
      </div>
    </div>

    <!-- ActionModal for simple notifications -->
    <action-modal
      :show="modalState.show"
      :title="modalState.title"
      :body="modalState.body"
      :buttons="modalState.buttons"
      @close="closeModal"
    />

    <!-- Send message modal for message sending -->
    <div class="send-message-modal-container" :style="{ display: sendMessageModalOpen ? 'flex' : 'none' }"
        @click="closeSendMessageModal">
      <div class="send-message-modal-wrapper">
        <span>Confirm Before Sending</span>
        <div class="modal-row">
          <div class="modal-row-heading">
            Included Segments
          </div>
          <div class="modal-row-content">
            {{ config.audience.includedAudience ? config.audience.includedAudience.map(item => item.name).join(', ') :
                ''
            }}
          </div>
        </div>
        <div class="modal-row">
          <div class="modal-row-heading">
            Recipients
          </div>
          <div v-if="typeof audienceCount == 'number'" class="modal-row-content">
            {{ audienceCount }}
          </div>
          <div v-else class="modal-row-content" style="color: red;">
            {{ audienceCount }}
          </div>
        </div>
        <div class="modal-row">
          <div class="modal-row-heading">
            Variant A Title
          </div>
          <div class="modal-row-content">
            {{ config.messages[0] && config.messages[0].messageBody.default.title }}
          </div>
        </div>
        <div class="modal-row">
          <div class="modal-row-heading">
            Variant A Content
          </div>
          <div class="modal-row-content">
            {{ config.messages[0] && config.messages[0].messageBody.default.body }}
          </div>
        </div>
        <div class="modal-row">
          <div class="modal-row-heading">
            Variant B Title
          </div>
          <div class="modal-row-content">
            {{ config.messages[1] && config.messages[1].messageBody.default.title }}
          </div>
        </div>
        <div class="modal-row">
          <div class="modal-row-heading">
            Variant B Content
          </div>
          <div class="modal-row-content">
            {{ config.messages[1] && config.messages[1].messageBody.default.body }}
          </div>
        </div>
        <div v-if="config.schedule.sendImmediately" class="modal-row">
          <div class="modal-row-heading">
            Scheduled For
          </div>
          <div class="modal-row-content">
            Right away
          </div>
        </div>
        <div v-else-if="!config.schedule.abSendTime" class="modal-row">
          <div class="modal-row-heading">
            Scheduled For
          </div>
          <div class="modal-row-content">
            {{ config.schedule.sendTime ? dateFormatter(config.schedule.sendTime) : '-' }}
          </div>
        </div>
        <template v-else>
          <div class="modal-row">
            <div class="modal-row-heading">
              Variant A Scheduled For
            </div>
            <div class="modal-row-content">
              {{ config.messages[0] && config.messages[0].sendTime ? dateFormatter(config.messages[0].sendTime) : '-' }}
            </div>
          </div>
          <div class="modal-row">
            <div class="modal-row-heading">
              Variant B Scheduled For
            </div>
            <div class="modal-row-content">
              {{ config.messages[1] && config.messages[1].sendTime ? dateFormatter(config.messages[1].sendTime) : '-' }}
            </div>
          </div>
        </template>
        <div class="modal-buttons">
          <div class="modal-button secondary" @click="closeSendMessageModal">
            CANCEL
          </div>
          <div class="modal-button primary" @click="() => { submitAutomatedABTest(false); closeSendMessageModal(); }">
            SEND MESSAGE
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InputGroup from "@/views/PushNotification/UIComponents/InputGroup";
import AudienceSection from "@/views/PushNotification/Components/Audience";
import MessageSection from "@/views/PushNotification/Components/Message";
import ScheduleSection from "@/views/PushNotification/Components/Schedule";
import notify from "@/util/notification";
import formatters from "@/util/formatters";
import Spinner from '../../../components/Spinner.vue';
import ActionModal from "@/views/PushNotification/UIComponents/ActionModal";
import { Datetime } from 'vue-datetime';

const isLocal = window.location.href.indexOf("localhost:8080") >= 0;
const audienceCountUrl = isLocal ? "http://localhost:5002/audienceCount" : "/audienceCount";
const cancelBigqueryJobUrl = isLocal ? "http://localhost:5002/audienceCount/cancel-bigquery-job" : "/audienceCount/cancel-bigquery-job";

export default {
  name: "AutomatedABTest",
  components: {
    InputGroup,
    AudienceSection,
    MessageSection,
    ScheduleSection,
    Spinner,
    ActionModal,
    Datetime
  },
  computed: {
    automatedABTests() {
      return this.$store.getters.automatedABTests
    },
    templates() {
      return this.$store.getters.templateMessages
    },
    appId() {
      return this.$store.getters.activeApp
    },
    draftButtonLabel() {
      return this.initialMessageState && (this.initialMessageState.status === 'ended' || this.initialMessageState.status === 'delivered') ? 'Duplicate and Save as Draft' : 'Save as Draft';
    },
    isConfirmDisabled() {
      return !this.permissionsLoaded || (this.initialMessageState && (this.initialMessageState.status === 'ended' || this.initialMessageState.status === 'delivered'));
    },
    variantASendTimeISO() {
      const t = this.config.messages[0] && this.config.messages[0].sendTime;
      if (t && typeof t === 'number') return new Date(t).toISOString();
      return '';
    },
    variantBSendTimeISO() {
      const t = this.config.messages[1] && this.config.messages[1].sendTime;
      if (t && typeof t === 'number') return new Date(t).toISOString();
      return '';
    },
    variantDelayWarning() {
      const t0 = this.config.messages[0] && this.config.messages[0].sendTime;
      const t1 = this.config.messages[1] && this.config.messages[1].sendTime;
      const interval = this.config.schedule.interval;
      const intervalType = this.config.schedule.intervalType;
      if (t0 && t1 && interval && interval > 0) {
        const delayMs = Math.abs(t0 - t1);
        const intervalMs = interval * (intervalType === 'hours' ? 3600000 : 86400000);
        if (delayMs > intervalMs * 0.5) {
          return 'Warning: The delay between variant delivery times exceeds 50% of the automated interval. The later variant from one cycle may overlap with the earlier variant of the next cycle.';
        }
      }
      return null;
    }
  },
  mounted() {
    const AutomatedABTestId = this.$route.params.id;
    if (AutomatedABTestId) {
      const editAutomatedABTest = this.automatedABTests.find(automatedABTest => automatedABTest.id === AutomatedABTestId);
      if (editAutomatedABTest) {
        this.config = JSON.parse(JSON.stringify(editAutomatedABTest));
        if (this.config.schedule && typeof this.config.schedule.sendTime === 'string') {
          this.config.schedule.sendTime = Date.parse(this.config.schedule.sendTime);
          if (isNaN(this.config.schedule.sendTime)) {
            this.config.schedule.sendTime = Date.now();
          }
        }
        if (this.config.schedule && this.config.schedule.abSendTime === undefined) {
          const t0 = this.config.messages[0] && this.config.messages[0].sendTime;
          const t1 = this.config.messages[1] && this.config.messages[1].sendTime;
          const hb = (this.config.messages[0] && this.config.messages[0].isHoldback) || (this.config.messages[1] && this.config.messages[1].isHoldback);
          const inferred = (!!t0 && !!t1 && t0 !== t1) || !!hb;
          this.$set(this.config.schedule, 'abSendTime', inferred);
        }
        this.initialMessageState = JSON.parse(JSON.stringify(editAutomatedABTest));
        this.initialAudienceState = JSON.parse(JSON.stringify(editAutomatedABTest.audience));
      }

      if (editAutomatedABTest.status === 'ended') {
        notify('This Automated A/B Test message is already ended. If you want to make changes, you need to duplicate and edit it.', 'warning');
      }
      else if (editAutomatedABTest.status === 'delivered') {
        notify('This Automated A/B Test message is ongoing. If you want to make changes, you need to duplicate and edit it.', 'warning');
      }

      if (editAutomatedABTest.audience_count && editAutomatedABTest.audience_count.status === 'TIMEOUT') {
          this.handleTimeoutStateOnLoad();
      }

      if (editAutomatedABTest.audience_count && editAutomatedABTest.audience_count.status === 'ERROR') {
          this.handleErrorStateOnLoad();
      }
    }
  },
  data() {
    return {
      dateFormatter: formatters.dateFormatter,
      audienceCount: 0,
      sendMessageModalOpen: false,
      pendingJobId: null,
      showSpinner: false,
      config: {
        name: "",
        audience: {
          sendToAll: true,
          includedAudience: [],
          excludedAudience: [],
          percentage: 100
        },
        messages: [
          {
            messageBody: {
              default: {
                title: "Title",
                subtitle: "Subtitle",
                body: "Message"
              }
            },
            platformSettings: {
              badges: "dontSet",
              badgeCount: 0,
              sound: "default",
              mutableContent: false
            },
            otherSettings: {
              additionalData: [],
              timeToLive: "",
              category: ""
            },
            variantName: "A",
            sendTime: "",
            isHoldback: false
          },
          {
            messageBody: {
              default: {
                title: "Title",
                subtitle: "Subtitle",
                body: "Message"
              }
            },
            platformSettings: {
              badges: "dontSet",
              badgeCount: 0,
              sound: "default",
              mutableContent: false
            },
            otherSettings: {
              additionalData: [],
              timeToLive: "",
              category: ""
            },
            variantName: "B",
            sendTime: "",
            isHoldback: false
          }
        ],
        schedule: {
          sendImmediately: true,
          sendTime: "",
          endTime: "",
          withLocalTimezone: false,
          interval: null,
          intervalType: 'days',
          daysToSend: "-",
          abSendTime: false
        }
      },
      initialMessageState: null,
      initialAudienceState: null,
      isAudienceUpdated: false,
      modalState: { 
        show: false, 
        title: '', 
        body: '', 
        buttons: [] 
      },
    }
  },
  methods: {
    updateLanguages(data) {
      const languages = Object.keys(data)
      this.config.messages.forEach(message => {
        const messageBody = message.messageBody
        languages.forEach(language => {
          if (messageBody[language] == undefined) {
            this.$set(messageBody, language, {})
          }
        })
      })
    },
    updateVariantASendTime(value) {
      if (value) {
        const timestamp = Date.parse(value);
        if (!isNaN(timestamp)) {
          this.$set(this.config.messages[0], 'sendTime', timestamp);
        }
      }
    },
    updateVariantBSendTime(value) {
      if (value) {
        const timestamp = Date.parse(value);
        if (!isNaN(timestamp)) {
          this.$set(this.config.messages[1], 'sendTime', timestamp);
        }
      }
    },
    updateVariantAHoldback(value) {
      this.$set(this.config.messages[0], 'isHoldback', Boolean(value));
    },
    updateVariantBHoldback(value) {
      this.$set(this.config.messages[1], 'isHoldback', Boolean(value));
    },
    onAbSendTimeToggle(value) {
      const enabled = Boolean(value);
      this.$set(this.config.schedule, 'abSendTime', enabled);
      if (!enabled) {
        if (this.config.messages[0]) {
          this.$set(this.config.messages[0], 'sendTime', '');
          this.$set(this.config.messages[0], 'isHoldback', false);
        }
        if (this.config.messages[1]) {
          this.$set(this.config.messages[1], 'sendTime', '');
          this.$set(this.config.messages[1], 'isHoldback', false);
        }
      } else {
        const seed = this.config.schedule.sendTime || (Date.now() + 24 * 3600 * 1000);
        if (this.config.messages[0] && !this.config.messages[0].sendTime) {
          this.$set(this.config.messages[0], 'sendTime', seed);
        }
        if (this.config.messages[1] && !this.config.messages[1].sendTime) {
          this.$set(this.config.messages[1], 'sendTime', seed);
        }
      }
    },
    // --- MODAL HELPERS ---
    showModal(title, body, buttons) { this.modalState = { show: true, title, body, buttons }; },
    closeModal() { this.modalState.show = false; },
    closeSendMessageModal() { this.sendMessageModalOpen = false;  },
    showSendMessageModal(count) {
      this.audienceCount = count;
      this.sendMessageModalOpen = true;
    },
    validateUserInput() {
      if (!this.config.name) {
        notify('Name cannot be empty!', 'error')
        return false;
      }
      if (!this.config.audience.sendToAll && this.config.audience.includedAudience.length === 0 && this.config.audience.excludedAudience.length === 0) {
        notify('No audience is selected.', 'error')
        return false;
      }
      if (!this.config.messages[0] || !this.config.messages[1]) {
        notify('A/B test messages are not initialized.', 'error')
        return false;
      }
      if (this.config.messages[0].isHoldback && this.config.messages[1].isHoldback) {
        notify('Only one variant can be set as holdback.', 'error')
        return false;
      }
      if (!this.config.schedule.sendImmediately) {
        if (this.config.schedule.abSendTime) {
          const t0 = this.config.messages[0].sendTime;
          const t1 = this.config.messages[1].sendTime;
          if (!t0 || !t1) {
            notify('Please set delivery time for both variants.', 'error')
            return false;
          }
          if (t0 < Date.now() || t1 < Date.now()) {
            notify('Variant delivery time is in the past. Please choose a future time.', 'error')
            return false;
          }
        } else {
          const t = this.config.schedule.sendTime;
          if (!t) {
            notify('Please set the delivery time.', 'error')
            return false;
          }
          if (t < Date.now()) {
            notify('Delivery time is in the past. Please choose a future time.', 'error')
            return false;
          }
        }
      }
      if (!this.config.schedule.interval || this.config.schedule.interval <= 0) {
        notify('Interval must be set.', 'error')
        return false;
      }
      return true;
    },

    // --- PRIMARY GATEKEEPER METHOD ---
    async openConfirmModal() {
      console.log(this.config);
      if (!this.validateUserInput()) return;
      // Ensure permissions are loaded before checking
      if (!this.permissionsLoaded) {
        console.log('Permissions not loaded yet, waiting...');
        try {
          await this.initializePermissions();
          console.log('Permissions loaded, rechecking...');
        } catch (error) {
          console.error('Failed to load permissions:', error);
          return notify('Failed to load permissions. Please try again.', 'error')
        }
      }
      
      if (!this.canSendPushNotifications) {
        console.log('Permission denied: User cannot send push notifications');
        console.log('User groups:', this.$store.getters.user?.groups);
        console.log('User permissions:', this.userCapabilities);
        return notify('You are not authorized to send push notifications', 'error')
      }
      console.log('Permission granted: User can send push notifications');

      const audienceCountStatus = this.initialMessageState && this.initialMessageState.audience_count && this.initialMessageState.audience_count.status;
      if (audienceCountStatus === 'PENDING') {
        return notify('The audience count is pending. Please wait for it to complete.', 'warning');
      }

      if (this.isAudienceUpdated && this.initialMessageState.audience_count) {
        const body = `<p>You have updated the audience configuration. The audience count must be recalculated. This may change the A/B Test message's status to a draft.</p><p>Are you sure you want to continue?</p>`;
        this.showModal('Audience Updated', body, [
          { text: 'CANCEL', class: 'secondary', action: this.closeModal },
          { text: 'CONTINUE', class: 'primary', action: this.executeAudienceCalculation }
        ]);
      } else if (audienceCountStatus === 'DONE') {
        this.showSendMessageModal(this.initialMessageState.audience_count.value);
      } else {
        this.executeAudienceCalculation();
      }
    },
    // --- AUDIENCE CALCULATION ---
    executeAudienceCalculation() {
      this.closeModal();
      this.showSpinner = true;

      const requestBody = { 
        appId: this.appId, 
        includeds: this.config.audience.includedAudience, 
        excludeds: this.config.audience.excludedAudience, 
        isLookerPush: this.config.audience.isLookerPush 
      };
      
      // Include messageId if available (when editing existing message)
      if (this.initialMessageState?.id || this.$route.params.id) {
        requestBody.messageId = this.initialMessageState?.id || this.$route.params.id;
      }

      fetch(audienceCountUrl, {
        method: 'POST',
        body: JSON.stringify(requestBody),
        headers: { authorization: this.$store.getters.pushNotifApiKey, "Content-Type": "application/json" }
      }).then(res => res.json()).then(data => {
        this.showSpinner = false;
        if (data.isTimeout) {
          this.pendingJobId = data.jobId;
          this.showTimeoutModal();
        } else if (data.message === 'DONE') {
          const audienceCount = data.audienceCount.audienceCount;
          this.showSendMessageModal(audienceCount);
        } else {
          notify('Error getting audience count. Please try again.', 'error');
        }
      }).catch(() => {
        this.showSpinner = false;
        notify('Network error while getting audience count.', 'error');
      });
    },

    // --- TIMEOUT AND ERROR HANDLERS ---
    showTimeoutModal() {
      const body = `<p>The audience count is taking longer than expected. You can save this A/B Test message as a draft, and the count will be calculated in the background.</p>`;
      this.showModal('Audience Count Timeout', body, [
        { text: 'CANCEL', class: 'secondary', action: this.cancelBigqueryJob },
        { text: 'SAVE AS DRAFT & CALCULATE', class: 'primary', action: this.saveAsDraftWithPendingJob }
      ]);
    },
    async saveAsDraftWithPendingJob() {
      this.config.audience_count = { status: "PENDING", jobId: this.pendingJobId, value: null };
      
      if (this.initialMessageState) {
        await this.editAutomatedABTest(true);
      } else {
        await this.addAutomatedABTest(true);
      }
      
      this.pendingJobId = null;
      this.closeModal();
    },
    async cancelBigqueryJob() {
      if (this.pendingJobId) {
        await fetch(cancelBigqueryJobUrl, { method: 'POST', body: JSON.stringify({ jobId: this.pendingJobId, appId: this.appId }), headers: { authorization: this.$store.getters.pushNotifApiKey, "Content-Type": "application/json" } });
        this.pendingJobId = null;
      }
      this.closeModal();
      notify('Operation cancelled.', 'success');
    },
    handleErrorStateOnLoad() {
      const title = 'Audience Count Failed';
      const body = 'The previous audience count failed. Please try again.';
      this.showModal(title, body, [
        { text: 'OK', class: 'primary', action: this.closeModal }
      ]);
      this.config.audience_count = null;
      if (this.initialMessageState) this.initialMessageState.audience_count = null;
    },
    handleTimeoutStateOnLoad() {
      const isLooker = this.config.audience.isLookerPush;
      const title = 'Audience Count Timed Out';
      const body = isLooker
        ? 'The previous audience count timed out, likely due to an inefficient Looker query. Please review your Look with a BI Analyst.'
        : 'The previous audience count timed out. Please try again.';
      this.showModal(title, body, [
        { text: 'OK', class: 'primary', action: this.closeModal }
      ]);
      this.config.audience_count = null;
      if (this.initialMessageState) this.initialMessageState.audience_count = null;
    },
    // --- PAYLOAD PREPARATION HELPER ---
    prepareSchedulePayload(schedule) {
      const schedulePayload = { ...schedule };
      if (schedulePayload.sendImmediately) {
        schedulePayload.sendTime = Date.now();
        schedulePayload.abSendTime = false;
        if (this.config.messages[0]) {
          this.$set(this.config.messages[0], 'sendTime', '');
          this.$set(this.config.messages[0], 'isHoldback', false);
        }
        if (this.config.messages[1]) {
          this.$set(this.config.messages[1], 'sendTime', '');
          this.$set(this.config.messages[1], 'isHoldback', false);
        }
      } else if (!schedulePayload.abSendTime) {
        const shared = schedulePayload.sendTime || (Date.now() + 24 * 60 * 60 * 1000);
        schedulePayload.sendTime = shared;
        if (this.config.messages[0]) {
          this.$set(this.config.messages[0], 'sendTime', shared);
          this.$set(this.config.messages[0], 'isHoldback', false);
        }
        if (this.config.messages[1]) {
          this.$set(this.config.messages[1], 'sendTime', shared);
          this.$set(this.config.messages[1], 'isHoldback', false);
        }
      } else {
        // Sync schedule.sendTime from per-variant times
        const t0 = this.config.messages[0] && this.config.messages[0].sendTime;
        const t1 = this.config.messages[1] && this.config.messages[1].sendTime;
        if (t0 && t1) {
          schedulePayload.sendTime = Math.min(t0, t1);
        } else if (!schedulePayload.sendTime) {
          schedulePayload.sendTime = Date.now() + (24 * 60 * 60 * 1000);
        }
      }
      return schedulePayload;
    },
    submitAutomatedABTest(asDraft) {
      const shouldNotAllowConfirm = this.initialMessageState && (this.initialMessageState.status === 'ended' || this.initialMessageState.status === 'delivered');
      if (this.config.messages.length < 2)
        return notify('A/B Test messages are not initialized properly', 'error')
      if (!this.config.name)
        return notify('Name cannot be empty.', 'error')
      if (shouldNotAllowConfirm) {
        return this.duplicateAutomatedABTest();
      } else if (this.initialMessageState) {
        return this.editAutomatedABTest(asDraft);
      } else {
        return this.addAutomatedABTest(asDraft);
      }
    },
    addAutomatedABTest(asDraft) {
      this.showSpinner = true;
      const payload = { ...this.config }
      payload.statistics = {}
      payload.statistics[payload.messages[0].variantName] = {sent: 0, clicked:0}
      payload.statistics[payload.messages[1].variantName] = {sent: 0, clicked:0}

      payload.status = asDraft ? 'draft' : 'scheduled'
      payload.automated = true
      payload.schedule = this.prepareSchedulePayload(payload.schedule);
      if (this.audienceCount) {
        payload.audience_count = { status: 'DONE', value: this.audienceCount };
      }
      this.$store.dispatch('configAdd', { type: 'message', payload }).then((res) => {
        this.showSpinner = false;
        this.$router.push({ name: 'MessagesAutomatedABTests' });
        return res;
      }).catch(() => {
        this.showSpinner = false;
      });
    },
    editAutomatedABTest(asDraft) {
      this.showSpinner = true;
      let data = { ...this.config }
      data.statistics = {}
      data.statistics[data.messages[0].variantName] = {sent: 0, clicked:0}
      data.statistics[data.messages[1].variantName] = {sent: 0, clicked:0}
      data.status = asDraft ? 'draft' : 'scheduled'
      data.automated = true
      data.schedule = this.prepareSchedulePayload(data.schedule);
      if (this.audienceCount) {
        data.audience_count = { status: 'DONE', value: this.audienceCount };
      }
      if (!this.pendingJobId && this.initialMessageState?.audience_count && this.isAudienceUpdated) { // If the user saves the message as a draft while we were calculating its audience count, we need to cancel the job and reset the audience count.
        if (this.initialMessageState.audience_count.status === 'PENDING') {
          fetch(cancelBigqueryJobUrl, { method: 'POST', body: JSON.stringify({ jobId: this.initialMessageState.audience_count.jobId, appId: this.appId }), headers: { authorization: this.$store.getters.pushNotifApiKey, "Content-Type": "application/json" } });
        }
        delete data.audience_count;
      }
      const payload = { id: this.$route.params.id, data }
      this.$store.dispatch('configEdit', { type: 'message', payload }).then(() => {
        this.showSpinner = false;
        this.$router.push({ name: 'MessagesAutomatedABTests' });
      }).catch(() => {
        this.showSpinner = false;
      });
    },
    duplicateAutomatedABTest() {
      this.showSpinner = true;
      const payload = { ...this.config }
      payload.name += '_copy'
      payload.status = 'draft'
      payload.schedule = this.prepareSchedulePayload(payload.schedule);
      payload.statistics = {}
      payload.statistics[payload.messages[0].variantName] = { sent: 0, clicked: 0 }
      payload.statistics[payload.messages[1].variantName] = { sent: 0, clicked: 0 }
      payload.cloudTaskName = undefined
      delete payload.id
      payload.schedule.endTime = ""
      if (this.initialMessageState?.audience_count) {
        delete payload.audience_count;
      }
      return this.$store.dispatch('configAdd', { type: 'message', payload }).then((res) => {
        this.showSpinner = false;
        this.$router.push({ name: 'MessagesAutomatedABTests' });
        return res;
      }).catch(() => {
        this.showSpinner = false;
      });
    }
  },
  watch: {
    'config.audience': {
      handler(newValue) {
        if (!this.initialMessageState) return;
        if (JSON.stringify(newValue) !== JSON.stringify(this.initialAudienceState)) {
          if (!this.isAudienceUpdated && !(this.initialMessageState.status === 'delivered' || this.initialMessageState.status === 'ended') && this.initialMessageState.audience_count) {
            notify('Audience configuration has been updated. The audience count will be recalculated if you confirm.', 'warning', 'top-center');
          }
          this.isAudienceUpdated = true;
        } else {
          this.isAudienceUpdated = false;
        }
      },
      deep: true
    },
    'config.messages[0]': {
      handler(newVal, oldVal) {
        // Preserve variantName, sendTime, and isHoldback when template selection replaces the object
        if (newVal && !newVal.variantName) {
          this.$set(this.config.messages[0], 'variantName', (oldVal && oldVal.variantName) || 'A');
        }
        if (newVal && newVal.sendTime === undefined) {
          this.$set(this.config.messages[0], 'sendTime', (oldVal && oldVal.sendTime) || '');
        }
        if (newVal && newVal.isHoldback === undefined) {
          this.$set(this.config.messages[0], 'isHoldback', (oldVal && oldVal.isHoldback) || false);
        }
      },
      deep: false
    },
    'config.messages[1]': {
      handler(newVal, oldVal) {
        if (newVal && !newVal.variantName) {
          this.$set(this.config.messages[1], 'variantName', (oldVal && oldVal.variantName) || 'B');
        }
        if (newVal && newVal.sendTime === undefined) {
          this.$set(this.config.messages[1], 'sendTime', (oldVal && oldVal.sendTime) || '');
        }
        if (newVal && newVal.isHoldback === undefined) {
          this.$set(this.config.messages[1], 'isHoldback', (oldVal && oldVal.isHoldback) || false);
        }
      },
      deep: false
    },
    'config.messages': {
      handler(newMessages) {
        // Ensure messages array is always initialized
        if (!newMessages || newMessages.length === 0) {
          this.config.messages = [
          {
            messageBody: {
              default: {
                title: "Title",
                subtitle: "Subtitle",
                body: "Message"
              }
            },
            platformSettings: {
              badges: "dontSet",
              badgeCount: 0,
              sound: "default",
              mutableContent: false
            },
            otherSettings: {
              additionalData: [],
              timeToLive: "",
              category: ""
            },
            variantName: "A",
            sendTime: "",
            isHoldback: false
          },
          {
            messageBody: {
              default: {
                title: "Title",
                subtitle: "Subtitle",
                body: "Message"
              }
            },
            platformSettings: {
              badges: "dontSet",
              badgeCount: 0,
              sound: "default",
              mutableContent: false
            },
            otherSettings: {
              additionalData: [],
              timeToLive: "",
              category: ""
            },
            variantName: "B",
            sendTime: "",
            isHoldback: false
          }];
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
.message-buttons {
  display: flex;
  align-items: center;
}

.message-button {
  padding: 15px 25px;
  margin: 0 10px;
  cursor: pointer;
}

#button-confirm {
  border-radius: 28px;
  background: #BB86FC;
  color: white;
}

#button-confirm:disabled {
  background: #666;
  color: #ccc;
  cursor: not-allowed;
}

#button-draft {
  border-radius: 28px;
  border: 1px solid #BB86FC;
  color: white;
}


.send-message-modal-container {
  position: fixed;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.3);
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
}

.send-message-modal-wrapper {
  padding: 30px 20px;
  background-color: #25253A;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.send-message-modal-wrapper span {
  font-weight: bold;
  font-size: 18px;
  line-height: 16px;
  margin-bottom: 20px;
  /* identical to box height, or 67% */

  letter-spacing: 0.4px;

  /* Primary/200 */

  color: #BB86FC;
}

.modal-row {
  display: flex;
  width: 100%;
  margin: 20px 50px;
}

.modal-row-content {
  font-size: 16px;
  line-height: 16px;
  letter-spacing: 0.15px;
  color: #A5A5AB;
  width: 50%;
}

.modal-row-heading {
  font-size: 16px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #BB86FC;
  width: 50%;
  padding-right: 50px;
}

.modal-buttons {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.modal-button {
  margin: 30px 15px 15px;
  padding: 15px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 14px;
  line-height: 14px;
  /* identical to box height, or 89% */

  letter-spacing: 1.25px;
  cursor: pointer;
}

.modal-button.primary {
  background-color: #BB86FC;
  color: white;
}

.modal-button.secondary {
  color: #BB86FC;
}

.spinner {
  display: flex;
  position: fixed;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.3);
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  z-index: 9999;
}

.message-button.disabled {
  background: #666;
  color: #ccc;
  cursor: not-allowed;
  pointer-events: none;
}

#button-confirm.disabled {
  background: #666;
  color: #ccc;
}

.segment {
  background-color: rgba(44, 44, 71, 0.46);
  margin: 30px 0;
  border-radius: 3px;
  padding: 20px 20px;
  color: white;
  font-size: 16px;
  font-weight: 400;
}

.segment .title {
  font-size: 24px;
  font-weight: 500;
  margin-bottom: 15px;
}

.variant-settings-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 10px 0;
  flex-wrap: wrap;
}

.variant-schedule-label {
  color: #BB86FC;
  width: 100px;
  flex-shrink: 0;
  font-weight: 600;
}

.variant-holdback-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: white;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
}

.variant-holdback-toggle input[type="checkbox"] {
  margin: 0;
  cursor: pointer;
}

.ab-sendtime-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px 0;
  color: white;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
}

.ab-sendtime-toggle input[type="checkbox"] {
  margin: 0;
  cursor: pointer;
}
</style>